import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import LavvaDetailsWrapper from '../../../../../components/details-wrapper';
import { useBackdropContext } from '../../../../../context/backdrop';
import {
  AggregateVectorParameterTypeInternal,
  ChannelTypeInternal,
  MeasurementChannelKind,
  MeterTypeInternal,
  VersionedFeatureType,
} from '../../../../../data-access/gql-types/graphql';
import { isTariffComparatorDemo } from '../../../../../utils/helpers/demo';
import { useDevicesAndChannelsContext } from '../../../context/devices-and-channels';
import { useMeasurementMapOperations } from '../../../hooks/measurement-map/use-measurement-map-operations';
import { useCheckDeviceSupport } from '../../../hooks/version-map/use-check-device-support';
import { ChannelType, ChannelMeterType } from '../../../types';
import { MeterAnalysis } from './analysis';
import { Comparator } from './comparator/comparator';
import { MeterConfiguration } from './configuration/configuration';
import MeterDetailsProvider from './context';
import { MeterCurrent } from './current';
import { MeterMeasurements } from './measurements';
import { analizeAvailableMeasurementParameters } from './utils';

type ChannelDetailsMeterProps = {
  channel: ChannelType;
};

const ChannelDetailsMeter: React.FC<ChannelDetailsMeterProps> = ({ channel }) => {
  const { t } = useTranslation('channel-details');
  const { carouselDisabled } = useBackdropContext();
  const { getDeviceById } = useDevicesAndChannelsContext();
  const { convertNumberToMeasurement } = useMeasurementMapOperations();
  const { checkSupport } = useCheckDeviceSupport();
  const channelData = channel.data as ChannelMeterType;
  const device = getDeviceById(channel?.deviceId);

  const isStandalone =
    channelData.type === ChannelTypeInternal.Meter && channelData.meterType === MeterTypeInternal.Standalone;

  const analizeTabShouldDisplay = useMemo(() => {
    const supportedMappedParams = channelData.supportedAggregateVectorParameters.map(
      (param) =>
        convertNumberToMeasurement(MeasurementChannelKind.Meter)(
          'aggregates',
          param,
        ) as AggregateVectorParameterTypeInternal,
    );

    return supportedMappedParams.some((param) => analizeAvailableMeasurementParameters.includes(param));
  }, [channelData]);

  const tariffComparatorVisible = isTariffComparatorDemo && isStandalone && analizeTabShouldDisplay;

  const eventingTabVisible =
    checkSupport({
      feature: VersionedFeatureType.Eventing,
      supportKey: device?.payload.model,
      currentVersion: channel?.currentFirmwareVersion,
    }) && channelData.eventing !== null;

  return (
    <MeterDetailsProvider>
      <LavvaDetailsWrapper
        preventSwipeEvent={carouselDisabled}
        tabs={[
          { label: t('currently'), name: 'currently' },
          { label: t('measurement'), name: 'measurement' },
          ...(analizeTabShouldDisplay ? [{ label: t('analysis'), name: 'analysis' }] : []),
          ...(eventingTabVisible ? [{ label: t('configuration'), name: 'configuration' }] : []),
          ...(tariffComparatorVisible ? [{ label: t('comparator'), name: 'comparator' }] : []),
        ]}
        additionalTabs={[
          <MeterMeasurements key="2" channel={channel} />,
          ...(analizeTabShouldDisplay ? [<MeterAnalysis key="3" channel={channel} />] : []),
          ...(eventingTabVisible ? [<MeterConfiguration key="4" channel={channel} />] : []),
          ...(tariffComparatorVisible ? [<Comparator key="5" channel={channel} />] : []),
        ]}
      >
        <MeterCurrent channel={channel} />
      </LavvaDetailsWrapper>
    </MeterDetailsProvider>
  );
};

export default ChannelDetailsMeter;
